import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Lightning, Plus, Lock, ShieldCheck, Warning, X, Trash, Eye, FingerprintSimple, UploadSimple, FileArrowDown, FileDoc, FileXls, GitDiff, FilePdf, CaretDown } from "@phosphor-icons/react";

const TYPE_BADGE = {
  baseline: "bg-slate-100 text-slate-700 border-slate-300",
  pre_change: "bg-amber-100 text-amber-800 border-amber-300",
  post_change: "bg-blue-100 text-blue-800 border-blue-300",
};
const typeLabel = (t) => ({ baseline: "Baseline", pre_change: "Pre-Change", post_change: "Post-Change" }[t] || t);

export default function Cip010Baselines({ embedded = false }) {
  const { isRole } = useAuth();
  const canEdit = isRole("admin", "analyst");
  const isAdmin = isRole("admin");
  const [summary, setSummary] = useState(null);
  const [snaps, setSnaps] = useState([]);
  const [meta, setMeta] = useState({ snapshot_types: ["baseline", "pre_change", "post_change"], protocols: ["TCP", "UDP"], port_status: ["Listening", "Established", "Closed"] });
  const [assets, setAssets] = useState([]);
  const [filter, setFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [twJobId, setTwJobId] = useState(null);
  const [twPreview, setTwPreview] = useState(null);
  const [twBusy, setTwBusy] = useState(false);
  const [twMenu, setTwMenu] = useState(false);
  const [delta, setDelta] = useState(false);

  const loadSummary = useCallback(() => api.get("/cip010/summary").then((r) => setSummary(r.data)).catch(() => {}), []);
  const loadSnaps = useCallback((assetId) => {
    const q = assetId ? `?asset_id=${assetId}` : "";
    return api.get(`/cip010/baselines${q}`).then((r) => { setSnaps(r.data.snapshots); setMeta({ snapshot_types: r.data.snapshot_types, protocols: r.data.protocols, port_status: r.data.port_status }); }).catch(() => {});
  }, []);
  const loadAssets = useCallback(() => api.get("/cip010/assets").then((r) => setAssets(r.data.assets)).catch(() => {}), []);

  useEffect(() => { loadSummary(); loadAssets(); }, [loadSummary, loadAssets]);
  useEffect(() => { loadSnaps(filter); }, [filter, loadSnaps]);

  const verify = async (s) => {
    try {
      const { data } = await api.get(`/cip010/baseline/snapshot/${s.id}/verify`);
      if (data.verified) toast.success("Integrity verified — hash matches the value captured at creation.");
      else toast.error("INTEGRITY FAILURE — this snapshot has been altered since creation.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteSnap = async (s) => {
    if (!window.confirm(`Permanently delete this ${typeLabel(s.snapshot_type)} baseline for ${s.asset_name}?\n\nThis is a locked, tamper-evident record and cannot be recovered.`)) return;
    try {
      await api.delete(`/cip010/baseline/snapshot/${s.id}`);
      toast.success("Baseline permanently deleted");
      loadSnaps(filter); loadSummary(); loadAssets();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportDocx = async (s) => {
    const safeAsset = (s.asset_name || "asset").replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 40);
    const fname = `CIP010_R1_1_Baseline_${safeAsset}_${s.snapshot_type}_${s.id.slice(0, 8)}.docx`;
    try { await downloadBlob(`/cip010/baseline/snapshot/${s.id}/export`, fname); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportXlsx = async (s) => {
    const safeAsset = (s.asset_name || "asset").replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 40);
    const fname = `CIP010_R1_1_Baseline_${safeAsset}_${s.snapshot_type}_${s.id.slice(0, 8)}.xlsx`;
    try { await downloadBlob(`/cip010/baseline/snapshot/${s.id}/export.xlsx`, fname); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadBlankTemplate = async () => {
    try { await downloadBlob("/cip010/baseline-template", "CIP010_R1_1_Baseline_Template_BLANK.docx"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadBlankTemplateXlsx = async () => {
    try { await downloadBlob("/cip010/baseline-template.xlsx", "CIP010_R1_1_Baseline_Template_BLANK.xlsx"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/cip010/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.snapshots_created} snapshot(s)`);
      if (data.assets_not_found?.length) toast.warning(`${data.assets_not_found.length} asset name(s) not found in CIP-002: ${data.assets_not_found.slice(0, 3).join(", ")}`);
      loadSnaps(filter); loadSummary(); loadAssets();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doTripwire = async (e, snapshotType = "baseline") => {
    const file = e.target.files?.[0];
    e.target.value = "";
    setTwMenu(false);
    if (!file) return;
    setTwBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: started } = await api.post(`/cip010/import-tripwire/start?snapshot_type=${snapshotType}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = started.job_id;
      // poll for the parse result (large reports parse in the background — no proxy timeout)
      let ready = null;
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const { data } = await api.get(`/cip010/import-tripwire/status/${jobId}`);
        if (data.status === "ready") { ready = data; break; }
        if (data.status === "error") throw new Error(data.detail || "Could not parse the report.");
      }
      if (!ready) throw new Error("Parsing timed out. Please try again or use a smaller report.");
      setTwJobId(jobId);
      setTwPreview(ready);
      if (!ready.node_count) toast.warning("No nodes detected — check the diagnostics in the preview.");
    } catch (err) { toast.error(err.response ? formatApiError(err.response?.data?.detail) : err.message); }
    finally { setTwBusy(false); }
  };

  const commitTripwire = async (snapshotType, assignments) => {
    if (!twJobId) return;
    setTwBusy(true);
    const fd = new FormData();
    if (assignments) fd.append("assignments", JSON.stringify(assignments));
    try {
      const { data } = await api.post(`/cip010/import-tripwire/commit/${twJobId}?snapshot_type=${snapshotType}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.snapshots_created} snapshot(s) from Tripwire report`);
      if (data.nodes_unmatched?.length) toast.warning(`${data.nodes_unmatched.length} node(s) not assigned to a CIP-002 asset: ${data.nodes_unmatched.slice(0, 3).join(", ")}`);
      setTwPreview(null); setTwJobId(null);
      loadSnaps(filter); loadSummary(); loadAssets();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTwBusy(false); }
  };

  return (
    <div className="max-w-full p-6" data-testid="cip010-page">
      {!embedded && (<>
      <div className="flex items-center gap-2 mb-1">
        <Lightning size={26} weight="duotone" />
        <h1 className="text-2xl font-bold">CIP-010 Configuration Baselines</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Immutable, tamper-evident System of Record for Cyber Asset configuration baselines (CIP-010 R1). Each snapshot is
        SHA-256 hashed and stored write-once — updates are captured as new snapshots, never overwrites.
      </p>
      </>)}

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="cip010-summary">
          <Stat label="Snapshots" value={summary.snapshot_count} />
          <Stat label="Assets Baselined" value={`${summary.assets_with_baseline} / ${summary.total_assets}`} />
          <Stat label="Pre-Change" value={summary.by_type.pre_change || 0} />
          <Stat label="Baselines" value={summary.by_type.baseline || 0} />
          <Stat label="Integrity Failures" value={summary.integrity_failures} tone={summary.integrity_failures > 0 ? "text-red-600" : "text-green-600"} />
        </div>
      )}

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {canEdit && <Button size="sm" className="gap-1" onClick={() => setCreating(true)} data-testid="cip010-capture-btn"><Plus size={14} /> Capture Snapshot</Button>}
        {canEdit && <label className="inline-flex"><input type="file" accept=".csv" className="hidden" onChange={doImport} data-testid="cip010-import-input" /><Button size="sm" variant="outline" className="gap-1" asChild><span><UploadSimple size={14} /> Import CSV</span></Button></label>}
        {canEdit && (
          <div className="relative inline-flex">
            <Button size="sm" variant="outline" className="gap-1" disabled={twBusy} onClick={() => setTwMenu((v) => !v)} data-testid="cip010-tripwire-btn">
              <FileDoc size={14} /> {twBusy ? "Reading…" : "Import Tripwire (PDF/Word)"} <CaretDown size={12} />
            </Button>
            {twMenu && !twBusy && (
              <div className="absolute top-full left-0 mt-1 z-20 bg-card border border-border rounded-md shadow-lg w-60 py-1" data-testid="cip010-tripwire-menu">
                <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">Import this report as…</div>
                <label className="block px-3 py-2 text-sm hover:bg-secondary cursor-pointer" data-testid="tw-import-pre">
                  <input type="file" accept=".pdf,.docx" className="hidden" onChange={(e) => doTripwire(e, "pre_change")} />
                  <span className="font-medium">Pre-Change</span> <span className="text-xs text-muted-foreground">(before the work)</span>
                </label>
                <label className="block px-3 py-2 text-sm hover:bg-secondary cursor-pointer" data-testid="tw-import-post">
                  <input type="file" accept=".pdf,.docx" className="hidden" onChange={(e) => doTripwire(e, "post_change")} />
                  <span className="font-medium">Post-Change</span> <span className="text-xs text-muted-foreground">(after the work)</span>
                </label>
                <label className="block px-3 py-2 text-sm hover:bg-secondary cursor-pointer" data-testid="tw-import-baseline">
                  <input type="file" accept=".pdf,.docx" className="hidden" onChange={(e) => doTripwire(e, "baseline")} />
                  <span className="font-medium">Baseline</span> <span className="text-xs text-muted-foreground">(current state)</span>
                </label>
              </div>
            )}
          </div>
        )}
        {canEdit && <Button size="sm" variant="outline" className="gap-1" onClick={() => setDelta(true)} data-testid="cip010-delta-btn"><GitDiff size={14} /> Pre/Post Delta</Button>}
        <Button size="sm" variant="outline" className="gap-1" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/cip010/import-template`, "_blank")} data-testid="cip010-template-btn"><FileArrowDown size={14} /> CSV template</Button>
        <Button size="sm" variant="outline" className="gap-1" onClick={downloadBlankTemplate} data-testid="cip010-r11-template-btn" title="Blank CIP-010 R1.1 baseline document (Word) for SMEs to fill in by hand"><FileDoc size={14} /> R1.1 blank template (.docx)</Button>
        <Button size="sm" variant="outline" className="gap-1" onClick={downloadBlankTemplateXlsx} data-testid="cip010-r11-template-xlsx-btn" title="Blank CIP-010 R1.1 baseline template (Excel) — auditor-friendly, filterable columns"><FileXls size={14} /> R1.1 blank template (.xlsx)</Button>
        <select className="border border-border rounded px-2 py-1.5 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="cip010-asset-filter">
          <option value="">All assets</option>
          {assets.map((a) => <option key={a.id} value={a.id}>{a.name} — {a.bcs_name}</option>)}
        </select>
        <span className="text-xs text-muted-foreground">{snaps.length} snapshot(s)</span>
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="text-sm min-w-full" data-testid="cip010-table">
          <thead className="bg-secondary"><tr>{["", "Cyber Asset", "Type", "OS / Firmware", "Captured", "Author", "SHA-256", "Integrity", ""].map((h, i) => <th key={`hdr-${i}-${h}`} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {snaps.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground" data-testid="cip010-empty">No baseline snapshots yet.</td></tr>
            ) : snaps.map((s) => (
              <tr key={s.id} className="border-t border-border hover:bg-secondary/40" data-testid={`cip010-row-${s.id}`}>
                <td className="px-2 py-2 text-muted-foreground" title="Read-only (write-once)"><Lock size={14} weight="fill" /></td>
                <td className="px-3 py-2 font-medium">{s.asset_name}</td>
                <td className="px-3 py-2"><span className={`text-xs border px-1.5 py-0.5 rounded ${TYPE_BADGE[s.snapshot_type] || ""}`}>{typeLabel(s.snapshot_type)}</span></td>
                <td className="px-3 py-2">{s.os_firmware_version || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">{(s.snapshot_timestamp || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2">{s.author_name || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs" title={s.sha256_hash}>{(s.sha256_hash || "").slice(0, 12)}…</td>
                <td className="px-3 py-2">{s.verified
                  ? <span className="inline-flex items-center gap-1 text-xs text-green-700"><ShieldCheck size={14} weight="fill" /> Verified</span>
                  : <span className="inline-flex items-center gap-1 text-xs text-red-700 font-semibold"><Warning size={14} weight="fill" /> Tampered</span>}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <button onClick={() => setViewing(s)} title="View" data-testid={`cip010-view-${s.id}`} className="p-1 text-muted-foreground hover:text-foreground"><Eye size={16} /></button>
                    <button onClick={() => exportXlsx(s)} title="Export CIP-010 R1.1 workbook (.xlsx — filterable, no row cap)" data-testid={`cip010-export-xlsx-${s.id}`} className="p-1 text-muted-foreground hover:text-green-700"><FileXls size={16} /></button>
                    <button onClick={() => exportDocx(s)} title="Export CIP-010 R1.1 baseline document (.docx)" data-testid={`cip010-export-${s.id}`} className="p-1 text-muted-foreground hover:text-foreground"><FileDoc size={16} /></button>
                    <button onClick={() => verify(s)} title="Verify integrity" data-testid={`cip010-verify-${s.id}`} className="p-1 text-muted-foreground hover:text-foreground"><FingerprintSimple size={16} /></button>
                    {isAdmin && <button onClick={() => deleteSnap(s)} title="Delete (admin only)" data-testid={`cip010-delete-${s.id}`} className="p-1 text-muted-foreground hover:text-red-600"><Trash size={16} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <SnapshotForm assets={assets} meta={meta} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); loadSnaps(filter); loadSummary(); loadAssets(); }} />}
      {viewing && <SnapshotView snap={viewing} onClose={() => setViewing(null)} />}
      {twPreview && <TripwirePreview data={twPreview} meta={meta} assets={assets} busy={twBusy} onCommit={commitTripwire} onClose={() => { setTwPreview(null); setTwJobId(null); }} />}
      {delta && <DeltaModal assets={assets} onClose={() => setDelta(false)} />}
    </div>
  );
}

const Stat = ({ label, value, tone }) => (
  <div className="border border-border p-3" data-testid={`cip010-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="overline text-muted-foreground">{label}</div>
    <div className={`text-2xl font-bold ${tone || ""}`}>{value}</div>
  </div>
);

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-auto py-10" onClick={onClose}>
      <div className={`bg-card border border-border w-full ${wide ? "max-w-3xl" : "max-w-2xl"} mx-4`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} data-testid="modal-close"><X size={18} /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
const inputCls = "w-full border border-border rounded px-2 py-1.5 text-sm";

function SnapshotForm({ assets, meta, onClose, onSaved }) {
  const [f, setF] = useState({ asset_id: "", os_firmware_version: "", snapshot_type: "baseline", notes: "" });
  const [swCom, setSwCom] = useState([]);   // Commercial / open-source
  const [swCus, setSwCus] = useState([]);   // Custom software
  const [ports, setPorts] = useState([]);
  const [patches, setPatches] = useState([]);
  const [services, setServices] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.asset_id) return toast.error("Select a Cyber Asset");
    // Merge commercial + custom into installed_software with the is_custom flag
    const installed_software = [
      ...swCom.filter((s) => (s.name || "").trim()).map((s) => ({ ...s, is_custom: false })),
      ...swCus.filter((s) => (s.name || "").trim()).map((s) => ({ ...s, is_custom: true })),
    ];
    const payload = {
      ...f,
      installed_software,
      logical_ports: ports.filter((p) => (p.port_number || "").trim() || (p.service || "").trim()),
      security_patches: patches.filter((p) => (p.patch_id || "").trim() || (p.description || "").trim()),
      active_services: services.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
    };
    try { await api.post("/cip010/baseline/snapshot", payload); toast.success("Snapshot captured & hashed"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Modal title="Capture Baseline Snapshot (CIP-010 R1.1)" onClose={onClose} wide>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <label className="text-sm"><span className="block font-medium mb-1">Cyber Asset *</span>
          <select className={inputCls} value={f.asset_id} onChange={(e) => set("asset_id", e.target.value)} data-testid="cip010-form-asset" autoFocus>
            <option value="">Select…</option>
            {assets.map((a) => <option key={a.id} value={a.id}>{a.name} — {a.bcs_name} [{a.asset_type}]</option>)}
          </select>
        </label>
        <label className="text-sm"><span className="block font-medium mb-1">Snapshot type *</span>
          <select className={inputCls} value={f.snapshot_type} onChange={(e) => set("snapshot_type", e.target.value)} data-testid="cip010-form-type">
            {meta.snapshot_types.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
          </select>
        </label>
      </div>
      <label className="text-sm block mb-3"><span className="block font-medium mb-1">R1.1.1 — Operating System / Firmware version</span>
        <input className={inputCls} value={f.os_firmware_version} onChange={(e) => set("os_firmware_version", e.target.value)} data-testid="cip010-form-os" placeholder="e.g. Windows Server 2019 (build 17763)" /></label>

      <Repeater title="R1.1.2 — Commercial or open-source software" testid="sw" rows={swCom} setRows={setSwCom}
        cols={[
          { k: "software_type", ph: "Type (e.g. OS, DBMS, App)" },
          { k: "vendor", ph: "Vendor / Manufacturer" },
          { k: "name", ph: "Product" },
          { k: "version", ph: "Version" },
        ]}
        blank={{ software_type: "", vendor: "", name: "", version: "" }} />

      <Repeater title="R1.1.3 — Custom software" testid="swcus" rows={swCus} setRows={setSwCus}
        cols={[
          { k: "software_type", ph: "Type (e.g. Custom App, Script)" },
          { k: "vendor", ph: "Owner / Author" },
          { k: "name", ph: "Product / name" },
          { k: "version", ph: "Version" },
        ]}
        blank={{ software_type: "", vendor: "", name: "", version: "" }} />

      <Repeater title="R1.1.4 — Logical network-accessible ports & services" testid="ports" rows={ports} setRows={setPorts}
        cols={[
          { k: "protocol", ph: "Protocol", opts: meta.protocols },
          { k: "port_number", ph: "Port" },
          { k: "port_range", ph: "Port range" },
          { k: "process", ph: "Process" },
          { k: "service", ph: "Service" },
          { k: "need", ph: "Need / Justification" },
        ]}
        blank={{ protocol: "TCP", port_number: "", port_range: "", process: "", service: "", need: "" }} />

      <Repeater title="R1.1.5 — Security patches applied" testid="patches" rows={patches} setRows={setPatches}
        cols={[
          { k: "patch_id", ph: "Patch identifier (e.g. KB5036892)" },
          { k: "description", ph: "Description" },
          { k: "release_date", ph: "Release date (YYYY-MM-DD)" },
          { k: "install_date", ph: "Install date (YYYY-MM-DD)" },
        ]}
        blank={{ patch_id: "", description: "", release_date: "", install_date: "" }} />

      <label className="text-sm block mb-3"><span className="block font-medium mb-1">Active services (comma or line separated; optional)</span>
        <textarea className={inputCls} rows={2} value={services} onChange={(e) => setServices(e.target.value)} data-testid="cip010-form-services" placeholder="sshd, httpd, snmpd" /></label>

      <label className="text-sm block mb-3"><span className="block font-medium mb-1">Notes (optional)</span>
        <textarea className={inputCls} rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="cip010-form-notes" placeholder="Any context an auditor should know about this baseline." /></label>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={save} data-testid="cip010-form-save">Capture &amp; hash</Button>
      </div>
    </Modal>
  );
}

function Repeater({ title, testid, rows, setRows, cols, blank }) {
  const upd = (i, k, v) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows([...rows, { ...blank, _rk: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `r-${Date.now()}-${Math.random()}` }]);
  return (
    <div className="border border-border p-3 mb-3">
      <div className="flex items-center justify-between mb-2">
        <div className="overline text-muted-foreground">{title}</div>
        <Button size="sm" variant="outline" className="h-7 gap-1" onClick={addRow} data-testid={`cip010-add-${testid}`}><Plus size={12} /> Add</Button>
      </div>
      {rows.length === 0 ? <div className="text-xs text-muted-foreground">None added.</div> : rows.map((r, i) => (
        <div key={r._rk || `${testid}-${i}`} className="flex gap-2 mb-1.5 items-center">
          {cols.map((c) => c.opts ? (
            <select key={c.k} className="border border-border rounded px-2 py-1 text-sm flex-1" value={r[c.k]} onChange={(e) => upd(i, c.k, e.target.value)} data-testid={`cip010-${testid}-${c.k}-${i}`}>
              {c.opts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input key={c.k} className="border border-border rounded px-2 py-1 text-sm flex-1" placeholder={c.ph} value={r[c.k]} onChange={(e) => upd(i, c.k, e.target.value)} data-testid={`cip010-${testid}-${c.k}-${i}`} />
          ))}
          <button onClick={() => setRows(rows.filter((_, idx) => idx !== i))} className="p-1 text-red-500 hover:text-red-700"><Trash size={14} /></button>
        </div>
      ))}
    </div>
  );
}

function SnapshotView({ snap, onClose }) {
  return (
    <Modal title={`${snap.asset_name} — ${typeLabel(snap.snapshot_type)}`} onClose={onClose} wide>
      <div className="text-xs text-muted-foreground mb-3 space-y-0.5">
        <div>Captured {(snap.snapshot_timestamp || "").replace("T", " ").slice(0, 19)} by {snap.author_name}</div>
        <div className="flex items-center gap-1"><Lock size={12} /> Read-only · SHA-256 <span className="font-mono break-all">{snap.sha256_hash}</span></div>
        <div className={snap.verified ? "text-green-700" : "text-red-700 font-semibold"}>{snap.verified ? "Integrity verified" : "INTEGRITY FAILURE — altered since creation"}</div>
      </div>
      <Detail title="OS / Firmware">{snap.os_firmware_version || "—"}</Detail>
      <Detail title="Installed Software">
        {(snap.installed_software || []).length === 0 ? "—" : (
          <table className="text-xs w-full"><thead><tr className="text-muted-foreground">{["Name", "Version", "Vendor"].map((h) => <th key={h} className="text-left px-1 py-0.5">{h}</th>)}</tr></thead>
            <tbody>{snap.installed_software.map((s, i) => <tr key={`sw-${i}-${s.name}-${s.version}`} className={`border-t border-border/50 ${s.unauthorized ? "text-red-700" : ""}`}><td className="px-1 py-0.5">{s.name}{s.unauthorized ? " ⚠" : ""}</td><td className="px-1 py-0.5">{s.version}</td><td className="px-1 py-0.5">{s.vendor}</td></tr>)}</tbody></table>
        )}
      </Detail>
      {(snap.security_patches || []).length > 0 && (
        <Detail title="Security Patches">
          <table className="text-xs w-full"><thead><tr className="text-muted-foreground">{["Patch ID", "Description", "Date"].map((h) => <th key={h} className="text-left px-1 py-0.5">{h}</th>)}</tr></thead>
            <tbody>{snap.security_patches.map((p, i) => <tr key={`patch-${i}-${p.patch_id}`} className="border-t border-border/50"><td className="px-1 py-0.5 font-mono">{p.patch_id}</td><td className="px-1 py-0.5">{p.description}</td><td className="px-1 py-0.5">{p.date}</td></tr>)}</tbody></table>
        </Detail>
      )}
      <Detail title="Logical Ports">
        {(snap.logical_ports || []).length === 0 ? "—" : (
          <table className="text-xs w-full"><thead><tr className="text-muted-foreground">{["Protocol", "Port", "Status"].map((h) => <th key={h} className="text-left px-1 py-0.5">{h}</th>)}</tr></thead>
            <tbody>{snap.logical_ports.map((p, i) => <tr key={`port-${i}-${p.protocol}-${p.port_number}`} className={`border-t border-border/50 ${p.unauthorized ? "text-red-700" : ""}`}><td className="px-1 py-0.5">{p.protocol}</td><td className="px-1 py-0.5">{p.port_number}{p.unauthorized ? " ⚠" : ""}</td><td className="px-1 py-0.5">{p.status}</td></tr>)}</tbody></table>
        )}
      </Detail>
      <Detail title="Active Services">{(snap.active_services || []).join(", ") || "—"}</Detail>
      {snap.notes && <Detail title="Notes">{snap.notes}</Detail>}
    </Modal>
  );
}
const Detail = ({ title, children }) => (
  <div className="mb-3"><div className="overline text-muted-foreground mb-1">{title}</div><div className="text-sm">{children}</div></div>
);

function TripwirePreview({ data, meta, assets, busy, onCommit, onClose }) {
  const [type, setType] = useState(data.snapshot_type || "baseline");
  const [showDiag, setShowDiag] = useState(false);
  const nodes = data.nodes || [];
  const [assign, setAssign] = useState(() => {
    const m = {};
    nodes.forEach((n) => { m[n.node_name] = n.matched_asset_id || ""; });
    return m;
  });
  const assignedCount = nodes.filter((n) => assign[n.node_name]).length;
  return (
    <Modal title="Tripwire Report — Import Preview" onClose={onClose} wide>
      <p className="text-xs text-muted-foreground mb-3" data-testid="tw-preview-note">
        Parsed in-memory — nothing was saved yet. Assign each detected node to a CIP-002 Cyber Asset, then confirm to create one write-once snapshot per node.
      </p>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <label className="text-sm flex items-center gap-2"><span className="font-medium">Import as</span>
          <select className="border border-border rounded px-2 py-1.5 text-sm" value={type} onChange={(e) => setType(e.target.value)} data-testid="tw-snapshot-type">
            {(meta.snapshot_types || []).map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
          </select>
        </label>
        <span className="text-xs text-muted-foreground" data-testid="tw-match-summary">{nodes.length} node(s) detected · {assignedCount} assigned</span>
      </div>

      {nodes.length === 0 && <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3" data-testid="tw-no-nodes">No nodes detected. Expand diagnostics below to see how the report was read, then share it so the parser can be tuned.</div>}

      {nodes.map((n, i) => (
        <div key={n.node_name || `tw-node-${i}`} className="border border-border rounded mb-3" data-testid={`tw-node-${i}`}>
          <div className="flex items-center justify-between px-3 py-2 bg-secondary gap-2">
            <div className="font-semibold text-sm truncate">{n.node_name}</div>
            <select className="border border-border rounded px-2 py-1 text-xs bg-card" value={assign[n.node_name] || ""}
              onChange={(e) => setAssign((p) => ({ ...p, [n.node_name]: e.target.value }))} data-testid={`tw-assign-${i}`}>
              <option value="">— assign to CIP-002 asset —</option>
              {(assets || []).map((a) => <option key={a.id} value={a.id}>{a.name} — {a.bcs_name}</option>)}
            </select>
          </div>
          <div className="p-3 text-sm space-y-1">
            <div><span className="text-muted-foreground">OS / Firmware:</span> {n.os_firmware_version || "—"}</div>
            <div className="flex flex-wrap gap-4 text-xs pt-1">
              <span>Software: <b>{n.counts?.software || 0}</b>{n.counts?.unauthorized_software ? <span className="text-red-600"> ({n.counts.unauthorized_software} unauthorized)</span> : null}</span>
              <span>Patches: <b>{n.counts?.patches || 0}</b></span>
              <span>Ports: <b>{n.counts?.ports || 0}</b>{n.counts?.unauthorized_ports ? <span className="text-red-600"> ({n.counts.unauthorized_ports} unauthorized)</span> : null}</span>
              <span>Services: <b>{n.counts?.services || 0}</b></span>
            </div>
          </div>
        </div>
      ))}

      <button className="text-xs text-muted-foreground underline mb-3" onClick={() => setShowDiag((s) => !s)} data-testid="tw-toggle-diag">
        {showDiag ? "Hide" : "Show"} parser diagnostics
      </button>
      {showDiag && (
        <pre className="text-[11px] bg-secondary/60 border border-border rounded p-2 overflow-auto max-h-48 mb-3" data-testid="tw-diagnostics">
{JSON.stringify(data.diagnostics, null, 2)}
        </pre>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-border">
        <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button size="sm" onClick={() => onCommit(type, assign)} disabled={busy || assignedCount === 0} data-testid="tw-commit-btn">
          {busy ? "Importing…" : `Import ${assignedCount} node(s)`}
        </Button>
      </div>
    </Modal>
  );
}

function DeltaModal({ assets, onClose }) {
  const [assetId, setAssetId] = useState("");
  const [snaps, setSnaps] = useState([]);
  const [preId, setPreId] = useState("");
  const [postId, setPostId] = useState("");
  const [ignoreEph, setIgnoreEph] = useState(true);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const snapLabel = (s) => `${typeLabel(s.snapshot_type)} · ${(s.snapshot_timestamp || "").slice(0, 16).replace("T", " ")} · ${s.os_firmware_version ? s.os_firmware_version.slice(0, 28) : "—"}`;

  const onAsset = async (aid) => {
    setAssetId(aid); setRes(null); setErr(""); setPreId(""); setPostId(""); setSnaps([]);
    if (!aid) return;
    try {
      const { data } = await api.get(`/cip010/baselines?asset_id=${aid}`);
      const list = data.snapshots || [];
      setSnaps(list);
      // smart defaults: latest pre_change as "before", latest post_change as "after"
      const pre = list.find((s) => s.snapshot_type === "pre_change");
      const post = list.find((s) => s.snapshot_type === "post_change");
      if (pre) setPreId(pre.id);
      if (post) setPostId(post.id);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };

  const run = async () => {
    if (!assetId || !preId || !postId) return;
    setBusy(true); setErr(""); setRes(null);
    try {
      const { data } = await api.get(`/cip010/delta?asset_id=${assetId}&pre_id=${preId}&post_id=${postId}&ignore_ephemeral=${ignoreEph}`);
      setRes(data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      await downloadBlob(`/cip010/delta/report?asset_id=${assetId}&pre_id=${preId}&post_id=${postId}&ignore_ephemeral=${ignoreEph}`, "CIP010_Delta.pdf");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const d = res?.delta;
  const cf = res?.claroflow;
  return (
    <Modal title="Pre → Post Configuration Delta" onClose={onClose} wide>
      <div className="text-xs text-muted-foreground bg-secondary/50 border border-border rounded p-3 mb-4">
        <b>How to use:</b> 1) Pick the Cyber Asset. 2) Choose the <b>Before</b> and <b>After</b> snapshots to compare. 3) Click <b>Compare Changes</b>. Tip: when you import a Tripwire report, set its type to <b>Pre-Change</b> (before the work) or <b>Post-Change</b> (after) — those are auto-selected here.
      </div>
      <label className="text-sm block mb-3"><span className="block font-medium mb-1">1. Cyber Asset</span>
        <select className="border border-border rounded px-2 py-1.5 text-sm w-full max-w-md" value={assetId} onChange={(e) => onAsset(e.target.value)} data-testid="delta-asset-select">
          <option value="">Select an asset…</option>
          {(assets || []).map((a) => <option key={a.id} value={a.id}>{a.name} — {a.bcs_name}</option>)}
        </select>
      </label>

      {assetId && snaps.length < 2 && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 mb-3" data-testid="delta-need-two">
          This asset has {snaps.length} snapshot(s). You need at least two (a "before" and an "after") to compare. Import or capture another baseline for this asset first.
        </div>
      )}

      {snaps.length >= 2 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <label className="text-sm"><span className="block font-medium mb-1">2. Before (Pre-Change)</span>
            <select className="border border-border rounded px-2 py-1.5 text-sm w-full" value={preId} onChange={(e) => setPreId(e.target.value)} data-testid="delta-pre-select">
              <option value="">Select the "before" snapshot…</option>
              {snaps.map((s) => <option key={s.id} value={s.id}>{snapLabel(s)}</option>)}
            </select>
          </label>
          <label className="text-sm"><span className="block font-medium mb-1">3. After (Post-Change)</span>
            <select className="border border-border rounded px-2 py-1.5 text-sm w-full" value={postId} onChange={(e) => setPostId(e.target.value)} data-testid="delta-post-select">
              <option value="">Select the "after" snapshot…</option>
              {snaps.map((s) => <option key={s.id} value={s.id}>{snapLabel(s)}</option>)}
            </select>
          </label>
        </div>
      )}

      {snaps.length >= 2 && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <Button size="sm" onClick={run} disabled={!preId || !postId || preId === postId || busy} data-testid="delta-run-btn">{busy ? "Comparing…" : "Compare Changes"}</Button>
          <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={ignoreEph} onChange={(e) => { setIgnoreEph(e.target.checked); }} data-testid="delta-ephemeral-toggle" /> Ignore ephemeral ports & per-session services (noise)</label>
          {preId && postId && preId === postId && <span className="text-xs text-red-600">Choose two different snapshots.</span>}
          {res && <Button size="sm" variant="outline" className="gap-1" onClick={downloadPdf} data-testid="delta-pdf-btn"><FilePdf size={14} /> Download PDF</Button>}
        </div>
      )}

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="delta-error">{err}</div>}

      {res && (
        <div data-testid="delta-result">
          <div className={`rounded p-3 mb-3 border ${cf.authorized ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`} data-testid="delta-auth">
            <div className="text-sm font-semibold flex items-center gap-2">
              {cf.authorized ? <ShieldCheck size={16} weight="fill" className="text-green-700" /> : <Warning size={16} weight="fill" className="text-red-700" />}
              {cf.authorized ? "AUTHORIZED" : "NOT AUTHORIZED"}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">{cf.message}</div>
          </div>
          <div className="text-xs text-muted-foreground mb-3">
            {d.total_changes} change(s){d.ignored_ephemeral_ports ? " · noise filtered" : ""} · Before {(res.meta.pre.timestamp || "").slice(0, 16).replace("T", " ")} → After {(res.meta.post.timestamp || "").slice(0, 16).replace("T", " ")}
          </div>
          <DeltaSection title="OS / Firmware" items={d.os_change ? [`Changed: ${d.os_change.from} → ${d.os_change.to}`] : []} />
          <DeltaSection title="Software" items={[
            ...d.software.added.map((s) => `+ ${s.name} ${s.version || ""}`),
            ...d.software.removed.map((s) => `− ${s.name} ${s.version || ""}`),
            ...d.software.modified.map((m) => `~ ${m.name} (${m.from} → ${m.to})`),
          ]} />
          <DeltaSection title="Security Patches" items={[
            ...d.security_patches.added.map((p) => `+ ${p.patch_id} — ${p.description}`),
            ...d.security_patches.removed.map((p) => `− ${p.patch_id} — ${p.description}`),
          ]} />
          <DeltaSection title="Logical Ports" items={[
            ...d.ports.added.map((p) => `+ ${p.protocol}/${p.port_number}${p.unauthorized ? " (UNAUTHORIZED)" : ""} ${p.process || ""}`),
            ...d.ports.removed.map((p) => `− ${p.protocol}/${p.port_number} ${p.process || ""}`),
          ]} />
          <DeltaSection title="Services" items={[
            ...d.services.added.map((s) => `+ ${s}`),
            ...d.services.removed.map((s) => `− ${s}`),
          ]} />
        </div>
      )}
    </Modal>
  );
}

const DeltaSection = ({ title, items }) => (
  <div className="mb-3">
    <div className="overline text-muted-foreground mb-1">{title}</div>
    {items.length === 0 ? <div className="text-xs text-muted-foreground italic">No changes.</div>
      : <ul className="text-sm space-y-0.5">{items.map((t, i) => (
          <li key={`delta-${i}-${t.slice(0, 40)}`} className={`${t.startsWith("+") ? "text-green-700" : t.startsWith("−") ? "text-red-700" : "text-amber-700"} font-mono text-xs`}>{t}</li>
        ))}</ul>}
  </div>
);

